import { useNavigate } from "@tanstack/react-router";
import {
  PaletteIcon,
  Settings2Icon,
  SparklesIcon,
  UsersIcon,
} from "lucide-react";
import { useEffect } from "react";

import { Button } from "@/components/button";
import {
  Dialog,
  DialogBody,
  DialogCloseButton,
  DialogFooter,
} from "@/components/dialog";
import { Modal } from "@/components/modal";
import { useRecommendedProduct } from "@/hooks/useRecommendedProduct";
import { useUser } from "@/hooks/useUser";

interface UpsellModalProps {
  isOpen: boolean;
  onOpenChange: (isOpen: boolean) => void;
  onUpgrade?: () => void;
  type?: "draft" | "pause" | "regular";
}

const modalText = {
  draft: {
    body: "You’re almost there! Upgrade now and we’ll start working on your design.",
    header: "Design brief created!",
  },
  pause: {
    body: "The pause plan has limited access to user functionality. Upgrade to unlock full potential of our platform.",
    header: "Unlock premium features",
  },
  regular: {
    body: "The kickstarter plan has limited access to user functionality. Upgrade to unlock full potential of our platform.",
    header: "Unlock premium features",
  },
};

const features = [
  {
    icon: <PaletteIcon className="text-fg-brand-primary" size={24} />,
    title: "Choose your design capabilities",
  },
  {
    icon: <Settings2Icon className="text-fg-brand-primary" size={24} />,
    title: "Decide the design volume",
  },
  {
    icon: <UsersIcon className="text-fg-brand-primary" size={24} />,
    title: "Experience a seamless and scalable design partnership.",
  },
];

export function UpsellModal({
  isOpen,
  onOpenChange,
  onUpgrade,
  type = "regular",
}: UpsellModalProps) {
  const navigate = useNavigate();
  const { data: userData } = useUser();
  const projectId = userData?.currentProjectId;

  const {
    data: recommendedProduct,
    isLoading,
    refetch,
  } = useRecommendedProduct({
    projectId: projectId || 0,
  });

  useEffect(() => {
    if (isOpen && projectId) {
      refetch();
    }
  }, [isOpen, projectId, refetch]);

  const product = recommendedProduct || {
    product: {
      allowedRequestTypes: "graphic_and_video",
      description: "Professional graphic design services",
      features: [
        "Choose your design capabilities",
        "Decide the design volume",
        "Experience a seamless and scalable design partnership.",
      ],
      id: 0,
      monthlyPrice: 500,
      stripeMonthlyProductId: null,
      title: "Digital Ads - Growth",
    },
    recommendation: {
      basedOn: ["Default recommendation"],
      confidence: 70,
      reason: "No drafts available for analysis",
    },
  };

  const handleUpgrade = () => {
    onUpgrade?.();

    if (product.product.stripeMonthlyProductId && userData?.user?.email) {
      navigate({
        search: {
          price: product.product.monthlyPrice
            ? `$${Number(product.product.monthlyPrice).toLocaleString("en-US")}/mo`
            : null,
          product_id: product.product.stripeMonthlyProductId,
          receipt_email: userData.user.email,
        },
        to: "/subscription",
      });
    } else {
      navigate({ to: "/pricing" });
    }
  };

  const handleClose = () => {
    if (type === "pause") {
      onOpenChange(false);
    } else {
      onOpenChange(false);
      navigate({ to: "/pricing" });
    }
  };

  return (
    <Modal
      classNames={{ modal: "w-[400px]" }}
      isDismissable
      isOpen={isOpen}
      onOpenChange={onOpenChange}
    >
      <Dialog>
        <DialogBody className="p-6">
          <DialogCloseButton />

          <div className="flex flex-col gap-xl mb-4">
            <div className="size-12 shrink-0 grid place-items-center rounded-full bg-bg-brand-secondary">
              <SparklesIcon className="text-fg-brand-primary" size={24} />
            </div>

            <div className="flex flex-col gap-xs">
              <h2 className="text-lg font-semibold text-text-primary">
                {modalText[type].header}
              </h2>
              <p className="text-sm text-text-tertiary">
                {modalText[type].body}
              </p>
            </div>
          </div>

          <div className="flex flex-col gap-sm mb-6 border-[2px] border-[#6938EF] rounded-lg p-4">
            {isLoading ? (
              <div className="animate-pulse">
                <div className="h-6 bg-gray-200 rounded mb-2"></div>
                <div className="h-4 bg-gray-200 rounded mb-4"></div>
                <div className="space-y-2">
                  <div className="h-4 bg-gray-200 rounded"></div>
                  <div className="h-4 bg-gray-200 rounded"></div>
                  <div className="h-4 bg-gray-200 rounded"></div>
                </div>
              </div>
            ) : (
              <>
                <div className="flex items-center justify-between">
                  <h4 className="text-xl font-semibold">
                    {product.product.monthlyPrice
                      ? `$${Number(product.product.monthlyPrice).toLocaleString("en-US")}/mo`
                      : "Contact us"}
                  </h4>
                  <div className="border border-[#D9D6FE] bg-bg-brand-secondary rounded-full px-2 py-1 text-xs text-[#5925DC]">
                    RECOMMENDED
                  </div>
                </div>

                <div className="mb-4">
                  <h4 className="text-md font-semibold">
                    {product.product.title}
                  </h4>
                  <p className="text-sm text-text-tertiary">Billed monthly</p>
                </div>

                <div className="flex flex-col gap-2">
                  {product.product.features.map((feature, index) => (
                    <div className="flex items-center gap-2" key={index}>
                      <div className="size-6 shrink-0 grid place-items-center">
                        {features[index]?.icon || (
                          <SparklesIcon
                            className="text-fg-brand-primary"
                            size={24}
                          />
                        )}
                      </div>
                      <span className="text-sm font-medium text-text-primary">
                        {feature}
                      </span>
                    </div>
                  ))}
                </div>
              </>
            )}
          </div>
        </DialogBody>

        <DialogFooter className="px-6 pb-6 pt-0">
          <div className="flex flex-col gap-sm w-full">
            <Button
              className="w-full"
              onPress={handleUpgrade}
              size="lg"
              variant="primary"
            >
              Upgrade now
            </Button>

            <Button
              className="w-full"
              color="gray"
              onPress={handleClose}
              size="lg"
              variant="secondary"
            >
              {type === "pause" ? "Maybe later" : "See more plans"}
            </Button>
          </div>
        </DialogFooter>
      </Dialog>
    </Modal>
  );
}
